/**
 * Numero de vacantes de una requisicion (B1).
 *
 * El campo se edita en tres pantallas —alta, detalle y respuesta a una
 * requisicion devuelta— y antes cada una traia su propio minimo suelto. El
 * tope vive aqui para que las tres topen donde topa el backend.
 *
 * El dato viaja como `numeroPlazas` en la API: B1 cambia el vocabulario de la
 * pantalla, no el contrato, que ya esta conectado con el dashboard y los
 * reportes.
 */

/** El mismo numero que valida CreateJobRequestDto en el backend. */
export const MAXIMO_DE_VACANTES = 3

export const MINIMO_DE_VACANTES = 1

export const errorDeVacantes = (valor?: number | null): string | undefined => {
  if (valor === undefined || valor === null || Number.isNaN(valor)) {
    return undefined // el campo es opcional; sin valor el backend pone 1
  }
  if (!Number.isInteger(valor)) return 'El número de vacantes debe ser un número entero'
  if (valor < MINIMO_DE_VACANTES) return `Debe ser al menos ${MINIMO_DE_VACANTES} vacante`
  if (valor > MAXIMO_DE_VACANTES) {
    return `No se pueden solicitar más de ${MAXIMO_DE_VACANTES} vacantes`
  }
  return undefined
}

/**
 * El valor del input, acotado al rango permitido.
 *
 * La pantalla de respuesta a una requisicion devuelta no tiene donde pintar un
 * error, asi que ahi el campo se acota en vez de avisar: el usuario ve el
 * numero corregirse solo y no se queda con un envio que el backend rechazaria.
 */
export const vacantesDesdeInput = (valor: string): number => {
  const n = parseInt(valor, 10)
  if (Number.isNaN(n)) return MINIMO_DE_VACANTES
  return Math.min(Math.max(n, MINIMO_DE_VACANTES), MAXIMO_DE_VACANTES)
}

type ValidezDeVacantes = Pick<
  ValidityState,
  'rangeOverflow' | 'rangeUnderflow' | 'stepMismatch' | 'badInput'
>

/**
 * El aviso del navegador para el input, en espanol.
 *
 * El alta es un `form` nativo: con `max` puesto, Chrome frena el envio antes
 * de que corra la validacion propia y su texto sale en el idioma del
 * navegador. Cadena vacia es lo que setCustomValidity necesita para dar el
 * campo por bueno.
 */
export const avisoNativoDeVacantes = (validez: ValidezDeVacantes): string => {
  if (validez.rangeOverflow) return `No se pueden solicitar más de ${MAXIMO_DE_VACANTES} vacantes`
  if (validez.rangeUnderflow) return `Debe ser al menos ${MINIMO_DE_VACANTES} vacante`
  if (validez.stepMismatch) return 'El número de vacantes debe ser un número entero'
  if (validez.badInput) return 'Escribe el número de vacantes con dígitos'
  return ''
}
